import { NormalizedLandmark } from '@mediapipe/tasks-vision';

// Define keypoint indices for pose landmarks
const KEYPOINT_DICT = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32
};

// Define connections for drawing pose
const POSE_CONNECTIONS = [
  // Face
  [KEYPOINT_DICT.NOSE, KEYPOINT_DICT.LEFT_EYE_INNER],
  [KEYPOINT_DICT.LEFT_EYE_INNER, KEYPOINT_DICT.LEFT_EYE],
  [KEYPOINT_DICT.LEFT_EYE, KEYPOINT_DICT.LEFT_EYE_OUTER],
  [KEYPOINT_DICT.LEFT_EYE_OUTER, KEYPOINT_DICT.LEFT_EAR],
  [KEYPOINT_DICT.NOSE, KEYPOINT_DICT.RIGHT_EYE_INNER],
  [KEYPOINT_DICT.RIGHT_EYE_INNER, KEYPOINT_DICT.RIGHT_EYE],
  [KEYPOINT_DICT.RIGHT_EYE, KEYPOINT_DICT.RIGHT_EYE_OUTER],
  [KEYPOINT_DICT.RIGHT_EYE_OUTER, KEYPOINT_DICT.RIGHT_EAR],
  [KEYPOINT_DICT.MOUTH_LEFT, KEYPOINT_DICT.MOUTH_RIGHT],

  // Upper body
  [KEYPOINT_DICT.LEFT_SHOULDER, KEYPOINT_DICT.RIGHT_SHOULDER],
  [KEYPOINT_DICT.LEFT_SHOULDER, KEYPOINT_DICT.LEFT_ELBOW],
  [KEYPOINT_DICT.RIGHT_SHOULDER, KEYPOINT_DICT.RIGHT_ELBOW],
  [KEYPOINT_DICT.LEFT_ELBOW, KEYPOINT_DICT.LEFT_WRIST],
  [KEYPOINT_DICT.RIGHT_ELBOW, KEYPOINT_DICT.RIGHT_WRIST],

  // Hands
  [KEYPOINT_DICT.LEFT_WRIST, KEYPOINT_DICT.LEFT_PINKY],
  [KEYPOINT_DICT.LEFT_WRIST, KEYPOINT_DICT.LEFT_INDEX],
  [KEYPOINT_DICT.LEFT_WRIST, KEYPOINT_DICT.LEFT_THUMB],
  [KEYPOINT_DICT.RIGHT_WRIST, KEYPOINT_DICT.RIGHT_PINKY],
  [KEYPOINT_DICT.RIGHT_WRIST, KEYPOINT_DICT.RIGHT_INDEX],
  [KEYPOINT_DICT.RIGHT_WRIST, KEYPOINT_DICT.RIGHT_THUMB],

  // Torso
  [KEYPOINT_DICT.LEFT_SHOULDER, KEYPOINT_DICT.LEFT_HIP],
  [KEYPOINT_DICT.RIGHT_SHOULDER, KEYPOINT_DICT.RIGHT_HIP],
  [KEYPOINT_DICT.LEFT_HIP, KEYPOINT_DICT.RIGHT_HIP],

  // Lower body
  [KEYPOINT_DICT.LEFT_HIP, KEYPOINT_DICT.LEFT_KNEE],
  [KEYPOINT_DICT.RIGHT_HIP, KEYPOINT_DICT.RIGHT_KNEE],
  [KEYPOINT_DICT.LEFT_KNEE, KEYPOINT_DICT.LEFT_ANKLE],
  [KEYPOINT_DICT.RIGHT_KNEE, KEYPOINT_DICT.RIGHT_ANKLE],

  // Feet
  [KEYPOINT_DICT.LEFT_ANKLE, KEYPOINT_DICT.LEFT_HEEL],
  [KEYPOINT_DICT.RIGHT_ANKLE, KEYPOINT_DICT.RIGHT_HEEL],
  [KEYPOINT_DICT.LEFT_ANKLE, KEYPOINT_DICT.LEFT_FOOT_INDEX],
  [KEYPOINT_DICT.RIGHT_ANKLE, KEYPOINT_DICT.RIGHT_FOOT_INDEX],
  [KEYPOINT_DICT.LEFT_HEEL, KEYPOINT_DICT.LEFT_FOOT_INDEX],
  [KEYPOINT_DICT.RIGHT_HEEL, KEYPOINT_DICT.RIGHT_FOOT_INDEX]
];

// Calculate angle between three points
export const calculateAngle = (a: NormalizedLandmark, b: NormalizedLandmark, c: NormalizedLandmark): number => {
  // Convert normalized coordinates to vectors
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };

  // Calculate dot product
  const dot = ab.x * cb.x + ab.y * cb.y;

  // Calculate magnitudes
  const magAB = Math.sqrt(ab.x * ab.x + ab.y * ab.y);
  const magCB = Math.sqrt(cb.x * cb.x + cb.y * cb.y);

  // Calculate angle in radians
  const angleRad = Math.acos(dot / (magAB * magCB));

  // Convert to degrees
  const angleDeg = angleRad * (180 / Math.PI);

  return angleDeg;
};

// Calculate relevant angles for exercise form analysis
export const calculateAngles = (keypoints: NormalizedLandmark[]) => {
  // Use right side by default, fall back to left if needed
  const useRight = keypoints[KEYPOINT_DICT.RIGHT_KNEE].visibility > 0.5 && 
                  keypoints[KEYPOINT_DICT.RIGHT_HIP].visibility > 0.5 && 
                  keypoints[KEYPOINT_DICT.RIGHT_ANKLE].visibility > 0.5;
  
  // Calculate knee angle
  const kneeAngle = useRight
    ? calculateAngle(
        keypoints[KEYPOINT_DICT.RIGHT_HIP],
        keypoints[KEYPOINT_DICT.RIGHT_KNEE],
        keypoints[KEYPOINT_DICT.RIGHT_ANKLE]
      )
    : calculateAngle(
        keypoints[KEYPOINT_DICT.LEFT_HIP],
        keypoints[KEYPOINT_DICT.LEFT_KNEE],
        keypoints[KEYPOINT_DICT.LEFT_ANKLE]
      );

  // Calculate hip angle
  const hipAngle = useRight
    ? calculateAngle(
        keypoints[KEYPOINT_DICT.RIGHT_SHOULDER],
        keypoints[KEYPOINT_DICT.RIGHT_HIP],
        keypoints[KEYPOINT_DICT.RIGHT_KNEE]
      )
    : calculateAngle(
        keypoints[KEYPOINT_DICT.LEFT_SHOULDER],
        keypoints[KEYPOINT_DICT.LEFT_HIP],
        keypoints[KEYPOINT_DICT.LEFT_KNEE]
      );

  // Calculate back angle (between shoulders and hips)
  const backAngle = calculateAngle(
    keypoints[KEYPOINT_DICT.LEFT_SHOULDER],
    keypoints[KEYPOINT_DICT.LEFT_HIP],
    keypoints[KEYPOINT_DICT.RIGHT_HIP]
  );

  return { kneeAngle, hipAngle, backAngle };
};

// Draw pose on canvas
export const drawPose = (
  keypoints: NormalizedLandmark[],
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number
) => {
  console.log(`drawPose called. Canvas: ${canvasWidth}x${canvasHeight}, Keypoints: ${keypoints.length}`); // Log entry
  // Draw connections
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'white';

  POSE_CONNECTIONS.forEach(([i, j]) => {
    const kp1 = keypoints[i];
    const kp2 = keypoints[j];

    // Check if keypoints are visible
    const visible = kp1.visibility && kp2.visibility && kp1.visibility > 0.3 && kp2.visibility > 0.3;
    // if (!visible) console.log(`Skipping connection ${i}-${j} due to low visibility: ${kp1.visibility?.toFixed(2)}, ${kp2.visibility?.toFixed(2)}`); // Optional detailed log
    if (visible) {
      ctx.beginPath();
      ctx.moveTo(kp1.x * canvasWidth, kp1.y * canvasHeight);
      ctx.lineTo(kp2.x * canvasWidth, kp2.y * canvasHeight);
      ctx.stroke();
    }
  });

  // Draw keypoints
  keypoints.forEach((keypoint) => {
    const visible = keypoint.visibility && keypoint.visibility > 0.3;
    // if (!visible) console.log(`Skipping keypoint due to low visibility: ${keypoint.visibility?.toFixed(2)}`); // Optional detailed log
    if (visible) {
      ctx.beginPath();
      ctx.arc(
        keypoint.x * canvasWidth,
        keypoint.y * canvasHeight,
        5,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = 'lime';
      ctx.fill();
    }
  });
};